import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import FieldLabel from '../components/FieldLabel';
import HtmlFieldWithLimit from '../components/HtmlFieldWithLimit';
import { outlinedFieldProps } from '../utils/fieldStyles';
import { trimTextOnBlur } from '../utils/formHelpers';
import stepsConfig from '../../stepsConfig';

const blankDay = (n) => ({
    day_number: n,
    day_title: '',
    description: '',
    line_items: [],
});

const blankLineItem = () => ({
    item_title: '',
    trip_component_map: '',
    location: '',
    sequence: 1,
    day_line_item_description: '',
});

/**
 * AE/SB Extension Days editor: count input + Day 1..N blocks with
 * title, description, and day line items (reuses itinerary line-item fields).
 *
 * Value: array of { day_number, day_title, description, line_items[] }
 */
const AddonExtensionScheduleField = ({
    field,
    data,
    change,
    keyPrefix = '',
    errors = [],
    renderRows,
    setErrors,
}) => {
    const days = Array.isArray(data[field.key]) ? data[field.key] : [];
    const count = days.length;
    const fieldPrefix = keyPrefix ? `${keyPrefix}.${field.key}` : field.key;

    const lineItemFields = useMemo(
        () => stepsConfig.addons?.lineItemFields || stepsConfig.itineraries?.lineItemFields || {},
        []
    );
    const lineItemRows = [
        ['item_title', 'trip_component_map', 'location'],
        ['sequence'],
        ['day_line_item_description'],
    ];

    const resizeDays = (nextCount) => {
        const n = Math.max(0, Math.floor(Number(nextCount) || 0));
        const next = [];
        for (let i = 0; i < n; i += 1) {
            const prev = days[i];
            next.push({
                ...(prev || blankDay(i + 1)),
                day_number: i + 1,
                line_items: Array.isArray(prev?.line_items) ? prev.line_items : [],
            });
        }
        change(field.key, next);
        // Keep Number of Days fields in sync (number_of_days + FE alias)
        change('number_of_days', n || '');
        change('extension_days_count', n || '');
        setErrors?.((prev) =>
            (prev || []).filter((e) => !String(e.key || '').startsWith(fieldPrefix))
        );
    };

    const updateDay = (dayIndex, key, value) => {
        const next = days.map((d, i) =>
            i === dayIndex ? { ...d, [key]: value, day_number: i + 1 } : { ...d, day_number: i + 1 }
        );
        change(field.key, next);
        const errKey = `${fieldPrefix}.${dayIndex}.${key}`;
        setErrors?.((prev) => (prev || []).filter((e) => e.key !== errKey));
    };

    const addLineItem = (dayIndex) => {
        const day = days[dayIndex] || blankDay(dayIndex + 1);
        const lineItems = [...(day.line_items || []), blankLineItem()];
        updateDay(dayIndex, 'line_items', lineItems);
    };

    const changeLineItem = (dayIndex, itemIndex, key, value) => {
        const day = days[dayIndex];
        if (!day) return;
        const lineItems = (day.line_items || []).map((li, i) =>
            i === itemIndex ? { ...li, [key]: value } : li
        );
        updateDay(dayIndex, 'line_items', lineItems);
        const errKey = `${fieldPrefix}.${dayIndex}.line_items.${itemIndex}.${key}`;
        setErrors?.((prev) => (prev || []).filter((e) => e.key !== errKey));
    };

    const removeLineItem = (dayIndex, itemIndex) => {
        const day = days[dayIndex];
        if (!day) return;
        const lineItems = (day.line_items || []).filter((_, i) => i !== itemIndex);
        updateDay(dayIndex, 'line_items', lineItems);
    };

    const countError = (errors || []).find((e) => e.key === `${keyPrefix ? `${keyPrefix}.` : ''}extension_days_count`);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        {...outlinedFieldProps}
                        type="number"
                        label={<FieldLabel label="Number of Days" />}
                        required
                        inputProps={{ min: 1, step: 1 }}
                        value={count > 0 ? count : ''}
                        onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') {
                                resizeDays(0);
                                return;
                            }
                            const parsed = Number(raw);
                            if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
                                return;
                            }
                            resizeDays(parsed);
                        }}
                        error={!!countError || (errors || []).some((e) => e.key === fieldPrefix)}
                        helperText={
                            countError?.message
                            || ((errors || []).find((e) => e.key === fieldPrefix)?.message)
                            || 'Whole number greater than 0'
                        }
                    />
                </Grid>
            </Grid>

            {days.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <MDTypography variant="subtitle2" fontWeight="medium" color="text">
                        Extension Day Schedule ({days.length} {days.length === 1 ? 'day' : 'days'})
                    </MDTypography>

                    {days.map((day, dayIndex) => {
                        const lineItems = day.line_items || [];
                        const titleErr = (errors || []).find(
                            (e) => e.key === `${fieldPrefix}.${dayIndex}.day_title`
                        );

                        return (
                            <Box
                                key={day.id ?? `ext-day-${dayIndex}`}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'grey.400',
                                    borderRadius: 0.5,
                                    overflow: 'hidden',
                                }}
                            >
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        bgcolor: 'grey.200',
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.400',
                                    }}
                                >
                                    <MDBox
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: '#D6E3FF',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="medium"
                                            sx={{ color: '#1565C8', fontSize: '0.7rem' }}
                                        >
                                            {day.day_number || dayIndex + 1}
                                        </MDTypography>
                                    </MDBox>
                                    <MDTypography variant="subtitle2" fontWeight="medium">
                                        Extension Day {day.day_number || dayIndex + 1}
                                    </MDTypography>
                                </MDBox>

                                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        {...outlinedFieldProps}
                                        label="Day Title"
                                        required
                                        value={day.day_title || ''}
                                        onChange={(e) => updateDay(dayIndex, 'day_title', e.target.value)}
                                        onBlur={(e) =>
                                            trimTextOnBlur(e.target.value, (next) =>
                                                updateDay(dayIndex, 'day_title', next)
                                            )
                                        }
                                        error={!!titleErr}
                                        helperText={titleErr?.message || ''}
                                    />

                                    <HtmlFieldWithLimit
                                        label="Description"
                                        value={day.description || ''}
                                        onChange={(value) => updateDay(dayIndex, 'description', value)}
                                        maxLen={2000}
                                    />

                                    {lineItems.map((lineItem, itemIndex) => {
                                        const itemPrefix = `${fieldPrefix}.${dayIndex}.line_items.${itemIndex}`;
                                        return (
                                            <MDBox
                                                key={lineItem.id ?? `li-${dayIndex}-${itemIndex}`}
                                                sx={{
                                                    p: 2,
                                                    borderLeft: '3px solid',
                                                    borderColor: '#1565C8',
                                                    borderRadius: 1,
                                                    bgcolor: 'rgba(21, 101, 200, 0.04)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 2,
                                                }}
                                            >
                                                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                                    <MDTypography
                                                        variant="caption"
                                                        fontWeight="medium"
                                                        sx={{ color: '#1565C8' }}
                                                    >
                                                        Day Line Item {itemIndex + 1}
                                                    </MDTypography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeLineItem(dayIndex, itemIndex)}
                                                        sx={{
                                                            color: 'text.secondary',
                                                            '&:hover': {
                                                                color: 'error.main',
                                                                bgcolor: 'rgba(186, 26, 26, 0.08)',
                                                            },
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </MDBox>
                                                {typeof renderRows === 'function' &&
                                                    renderRows(
                                                        lineItemRows,
                                                        lineItemFields,
                                                        lineItem,
                                                        (key, val) => changeLineItem(dayIndex, itemIndex, key, val),
                                                        itemPrefix
                                                    )}
                                            </MDBox>
                                        );
                                    })}

                                    <Box
                                        onClick={() => addLineItem(dayIndex)}
                                        sx={{
                                            borderRadius: 2,
                                            p: 1.25,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            cursor: 'pointer',
                                            border: '1px dashed',
                                            borderColor: 'grey.400',
                                            '&:hover': {
                                                borderColor: 'info.main',
                                                bgcolor: 'rgba(21, 101, 200, 0.04)',
                                            },
                                        }}
                                    >
                                        <AddIcon fontSize="small" color="info" />
                                        <MDTypography variant="button" fontWeight="medium" color="info">
                                            Add Day Line Item
                                        </MDTypography>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default AddonExtensionScheduleField;
